import { formatAppDateTime, formatShortDate } from "../shared/dateUtils"
import { IndicatorChip } from "../shared/IndicatorChip"
import closeIconUrl from "./icons/close.svg"
import type {
  StayCelebrationEntry,
  StayCelebrationTraveller,
  StayCelebrationType,
} from "./stayData"

type CelebrationsListProps = {
  isLoading: boolean
  isSaving: boolean
  occasions: readonly StayCelebrationEntry[]
  onRemoveOccasion: (occasion: StayCelebrationEntry) => void
  travellers: readonly StayCelebrationTraveller[]
}

export function CelebrationsList({
  isLoading,
  isSaving,
  occasions,
  onRemoveOccasion,
  travellers,
}: CelebrationsListProps) {
  const groupedOccasions = groupedByTarget(occasions, travellers)

  if (!groupedOccasions.length) {
    if (!isLoading) {
      return null
    }

    return (
      <p className="rounded-[4px] border border-dashed border-[#cfcfcf] bg-[#fafafa] px-3 py-2 text-sm font-medium leading-5 text-[#817d76]">
        Loading celebrations...
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {groupedOccasions.map((group) => (
        <section className="flex flex-col gap-2" key={group.key}>
          <h4 className="text-xs font-semibold uppercase leading-4 tracking-[0.72px] text-[#404040]">
            {group.label}
          </h4>
          <div className="flex flex-col gap-2">
            {group.occasions.map((occasion) => (
              <CelebrationEntry
                isSaving={isSaving}
                key={occasion.id}
                occasion={occasion}
                onRemoveOccasion={onRemoveOccasion}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function CelebrationEntry({
  isSaving,
  occasion,
  onRemoveOccasion,
}: {
  isSaving: boolean
  occasion: StayCelebrationEntry
  onRemoveOccasion: (occasion: StayCelebrationEntry) => void
}) {
  const dateLabel = occasion.occasion_date ? formatShortDate(occasion.occasion_date) : ""
  const note = occasion.note.trim()
  const summary = celebrationSummary(occasion)

  return (
    <article className="group flex min-w-0 items-center gap-2 rounded-[4px] border border-[#d4d4d4] bg-[#fafafa] px-3 py-2 hover:bg-[#f5f5f5]">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <IndicatorChip kind={occasion.occasion_type} />
          {dateLabel ? (
            <p className="ml-auto shrink-0 text-right text-xs font-normal leading-4 text-black">{dateLabel}</p>
          ) : null}
        </div>

        {note ? (
          <p className="text-sm font-normal leading-[18px] text-black">{note}</p>
        ) : null}

        <p className="text-xs font-normal leading-normal text-[#737373]">
          Captured by {occasion.created_by || "Unknown"}
          {occasion.created_at ? (
            <>
              <span aria-hidden="true"> · </span>
              <span>{formatAppDateTime(occasion.created_at)}</span>
            </>
          ) : null}
        </p>
      </div>

      <button
        aria-label={`Remove celebration: ${summary}`}
        className="grid size-4 shrink-0 place-items-center text-base leading-none text-[#737373] opacity-0 transition-opacity hover:text-black focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] group-hover:opacity-100 disabled:cursor-not-allowed disabled:text-[#a3a3a3]"
        disabled={isSaving}
        onClick={() => onRemoveOccasion(occasion)}
        type="button"
      >
        <img alt="" className="block size-[8.17px]" src={closeIconUrl} />
      </button>
    </article>
  )
}

function groupedByTarget(
  occasions: readonly StayCelebrationEntry[],
  travellers: readonly StayCelebrationTraveller[],
) {
  const groups: {
    key: string
    label: string
    occasions: StayCelebrationEntry[]
  }[] = []

  occasions.forEach((occasion) => {
    const key = occasion.traveller_ids.length ? [...occasion.traveller_ids].sort().join("|") : "whole-party"
    const existingGroup = groups.find((group) => group.key === key)

    if (existingGroup) {
      existingGroup.occasions.push(occasion)
      return
    }

    groups.push({
      key,
      label: targetLabel(occasion.traveller_ids, travellers),
      occasions: [occasion],
    })
  })

  return groups
}

function targetLabel(
  travellerIds: readonly string[],
  travellers: readonly StayCelebrationTraveller[],
) {
  if (!travellerIds.length) {
    return "Whole party"
  }

  return sentenceList(
    travellerIds.map((travellerId) =>
      travellers.find((traveller) => traveller.id === travellerId)?.display_name || travellerId,
    ),
  )
}

function sentenceList(values: readonly string[]) {
  if (values.length <= 2) return values.join(" and ")

  return `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`
}

function celebrationSummary(occasion: StayCelebrationEntry) {
  const typeLabel = celebrationTypeLabel(occasion.occasion_type)
  const dateLabel = occasion.occasion_date ? formatShortDate(occasion.occasion_date) : ""
  const heading = dateLabel ? `${typeLabel} (${dateLabel})` : typeLabel
  const note = occasion.note.trim()

  return [heading, note].filter(Boolean).join(" ")
}

function celebrationTypeLabel(occasionType: StayCelebrationType) {
  return occasionType
    .split("_")
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ")
}
